import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { FaChevronLeft, FaChevronRight, FaExpand, FaTimes } from 'react-icons/fa';
import { functions } from '../../firebase.config';
import { userMessageFromCallableError } from '../../lib/firebaseCallableErrors';
import { useDeckModal } from '../../contexts/DeckModalContext';
import {
  clearStoredFundDeckToken,
  FUND_DECK_TOKEN_STORAGE_KEY,
  getStoredFundDeckToken,
} from '../../lib/submitFundDeckRequest';
import '../../styles/kyro-design.css';
import './FundDeck.css';

type ViewerResponse =
  | { ok: false; code: 'SESSION_INVALID' | 'SESSION_EXPIRED' }
  | { ok: true; code: 'PROCESSING'; processingStatus?: string }
  | { ok: true; code: 'READY'; pageUrls: string[]; pageCount: number }
  | { ok: false; code: 'PROCESSING_ERROR'; message?: string };

const POLL_MS = 2500;
const SWIPE_THRESHOLD_PX = 56;

function formatDeckBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n > 100 * 1024 ? 0 : 1)} KB`;
  return `${(n / 1024 / 1024).toFixed(n > 10 * 1024 * 1024 ? 1 : 2)} MB`;
}

const FundDeck: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { openDeckModal } = useDeckModal();
  const lightboxRef = useRef<HTMLDivElement>(null);
  const lightboxOpenRef = useRef(false);
  const hadNativeLightboxFsRef = useRef(false);
  const qParam = (searchParams.get('t') ?? '').trim();

  useEffect(() => {
    if (!qParam) return;
    try {
      sessionStorage.setItem(FUND_DECK_TOKEN_STORAGE_KEY, qParam);
    } catch {
      /* private mode / quota */
    }
  }, [qParam]);

  const token = qParam || getStoredFundDeckToken() || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [sessionInvalid, setSessionInvalid] = useState(false);
  /** Slide-only lightbox (portal); browser fullscreen targets this root when supported. */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  /** After slide 1 is on screen, mount hidden <img>s for slides 2+ so they decode in the background. */
  const [bgPrefetchOn, setBgPrefetchOn] = useState(false);
  /** Per-slide decode complete (or error — don’t block progress). */
  const [slideDone, setSlideDone] = useState<boolean[]>([]);
  /** Optional byte sizes from HTTP HEAD (same-origin CORS may hide Content-Length). */
  const [slideByteSizes, setSlideByteSizes] = useState<(number | null)[] | null>(null);
  const [loadBarCompact, setLoadBarCompact] = useState(false);
  /** 0-based; reset when deck loads or token changes. */
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  /** Visible frame: cleared on slide / deck change until the active <img> fires onLoad (per-slide loader). */
  const [activeFrameReady, setActiveFrameReady] = useState(false);
  const swipeStartX = useRef<number | null>(null);

  /** Stable per deck URL set — avoids resetting slide index on unrelated `pages` reference churn. */
  const pagesKey = useMemo(() => (pages.length > 0 ? pages.join('\0') : ''), [pages]);

  useLayoutEffect(() => {
    setSessionInvalid(false);
    setError(null);
    setPages([]);
    setProcessing(false);
    setLoading(true);
    setSlideDone([]);
    setSlideByteSizes(null);
    setLoadBarCompact(false);
    setCurrentSlideIndex(0);
    setActiveFrameReady(false);
    setLightboxOpen(false);
    hadNativeLightboxFsRef.current = false;
    setBgPrefetchOn(false);
    document.body.style.overflow = '';
  }, [token]);

  useLayoutEffect(() => {
    if (!pagesKey) {
      setSlideDone([]);
      setBgPrefetchOn(false);
      return;
    }
    const n = pagesKey.split('\0').length;
    setSlideDone(Array.from({ length: n }, () => false));
    setLoadBarCompact(false);
    setCurrentSlideIndex(0);
    setActiveFrameReady(false);
  }, [pagesKey]);

  useEffect(() => {
    if (pages.length === 0) {
      setSlideByteSizes(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const sizes = await Promise.all(
        pages.map((url) =>
          fetch(url, { method: 'HEAD' })
            .then((r) => {
              const v = Number(r.headers.get('content-length'));
              return Number.isFinite(v) && v > 0 ? v : null;
            })
            .catch(() => null)
        )
      );
      if (!cancelled) setSlideByteSizes(sizes);
    })();
    return () => {
      cancelled = true;
    };
  }, [pages]);

  const goPrevSlide = useCallback(() => {
    setActiveFrameReady(false);
    setCurrentSlideIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNextSlide = useCallback(() => {
    setActiveFrameReady(false);
    setCurrentSlideIndex((i) => Math.min(pages.length - 1, i + 1));
  }, [pages.length]);

  useEffect(() => {
    if (pages.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveFrameReady(false);
        setCurrentSlideIndex((i) => Math.min(pages.length - 1, i + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveFrameReady(false);
        setCurrentSlideIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pages.length]);

  const onSwipeTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    swipeStartX.current = t != null ? t.clientX : null;
  };

  const onSwipeTouchEnd = (e: React.TouchEvent) => {
    const start = swipeStartX.current;
    swipeStartX.current = null;
    if (start == null || pages.length < 2) return;
    const end = e.changedTouches[0]?.clientX;
    if (end == null) return;
    const dx = end - start;
    if (dx < -SWIPE_THRESHOLD_PX) goNextSlide();
    else if (dx > SWIPE_THRESHOLD_PX) goPrevSlide();
  };

  const markSlideDone = useCallback((index: number) => {
    setSlideDone((prev) => {
      if (index < 0 || index >= prev.length) return prev;
      if (prev[index]) return prev;
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);

  const slidesReadyCount = slideDone.filter(Boolean).length;
  const loadComplete = pages.length > 0 && slidesReadyCount >= pages.length;
  const knownTotalBytes =
    slideByteSizes && slideByteSizes.length === pages.length
      ? slideByteSizes.reduce((a, s) => a + (s ?? 0), 0)
      : 0;
  const knownReadyBytes = slideDone.reduce((acc, ok, i) => {
    if (!ok) return acc;
    return acc + (slideByteSizes?.[i] ?? 0);
  }, 0);
  const progressPct =
    knownTotalBytes > 0
      ? Math.min(100, Math.round((knownReadyBytes / knownTotalBytes) * 100))
      : pages.length > 0
        ? Math.round((slidesReadyCount / pages.length) * 100)
        : 0;

  useEffect(() => {
    if (!loadComplete) {
      setLoadBarCompact(false);
      return;
    }
    const t = window.setTimeout(() => setLoadBarCompact(true), 2200);
    return () => clearTimeout(t);
  }, [loadComplete]);

  /** After at least one slide has decoded (usually slide 1), fetch/decode the rest in the background. */
  useEffect(() => {
    if (pages.length <= 1) return;
    if (slidesReadyCount >= 1) setBgPrefetchOn(true);
  }, [pages.length, slidesReadyCount]);

  const fetchViewer = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setPages([]);
      setProcessing(false);
      setSessionInvalid(false);
      return;
    }
    setError(null);
    setSessionInvalid(false);
    const fn = httpsCallable(functions, 'getFundDeckViewer', { timeout: 90_000 });
    try {
      const res = await fn({ token });
      const data = res.data as ViewerResponse;

      if (!data || typeof data !== 'object') {
        setError('Something went wrong. Try again shortly.');
        setLoading(false);
        return;
      }

      if (data.ok === false) {
        if (data.code === 'SESSION_INVALID' || data.code === 'SESSION_EXPIRED') {
          clearStoredFundDeckToken();
          setSessionInvalid(true);
          setPages([]);
          setProcessing(false);
          setLoading(false);
          return;
        }
        if (data.code === 'PROCESSING_ERROR') {
          setError(data.message || 'Deck processing failed. Contact compliance@kyrocapital.in.');
          setProcessing(false);
          setLoading(false);
          return;
        }
        setError('Unable to load the deck.');
        setLoading(false);
        return;
      }

      if (data.code === 'PROCESSING') {
        if (data.processingStatus === 'pages_need_regeneration') {
          setProcessing(false);
          setError(
            'This deck needs a quick refresh on our side. Please try again later or contact compliance@kyrocapital.in.'
          );
          setLoading(false);
          return;
        }
        setProcessing(true);
        setPages([]);
        setLoading(false);
        return;
      }

      if (data.code === 'READY' && Array.isArray(data.pageUrls)) {
        if (data.pageUrls.length === 0) {
          setError('Deck pages are not available yet.');
          setProcessing(false);
        } else {
          setPages(data.pageUrls);
          setProcessing(false);
        }
        setLoading(false);
        return;
      }

      setError('Unable to load the deck.');
      setLoading(false);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('NOT_FOUND') || msg.includes('not-found')) {
        setError('Service is updating. Try again in a few minutes.');
      } else {
        setError(userMessageFromCallableError(e));
      }
      setLoading(false);
      setProcessing(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchViewer();
  }, [fetchViewer]);

  useEffect(() => {
    if (!processing || !token) return;
    const quick = window.setTimeout(() => void fetchViewer(), 600);
    const id = window.setInterval(() => void fetchViewer(), POLL_MS);
    return () => {
      clearTimeout(quick);
      clearInterval(id);
    };
  }, [processing, token, fetchViewer]);

  const closeLightbox = useCallback(async () => {
    const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> };
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await doc.webkitExitFullscreen?.();
    } catch {
      /* */
    }
    hadNativeLightboxFsRef.current = false;
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const openLightbox = useCallback(() => {
    hadNativeLightboxFsRef.current = false;
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  useEffect(() => {
    const block = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', block);
    return () => document.removeEventListener('contextmenu', block);
  }, []);

  /** After lightbox mounts, ask for element fullscreen on the lightbox root (desktop); iOS keeps overlay-only. */
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    let cancelled = false;
    const run = async () => {
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      if (cancelled) return;
      const node = lightboxRef.current;
      if (!node) return;
      try {
        if (typeof node.requestFullscreen === 'function') {
          await node.requestFullscreen();
        }
      } catch {
        /* */
      }
      if (cancelled) return;
      try {
        const doc = document as Document & { webkitFullscreenElement?: Element | null };
        if (!document.fullscreenElement && !doc.webkitFullscreenElement) {
          const wk = (node as HTMLElement & { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen;
          if (typeof wk === 'function') wk.call(node);
        }
      } catch {
        /* */
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [lightboxOpen]);

  /** Leaving native fullscreen (e.g. Esc) closes the lightbox so the fixed overlay does not linger. */
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const doc = document as Document & { webkitFullscreenElement?: Element | null };
    const onFs = () => {
      const el = lightboxRef.current;
      const fs = document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
      if (el && fs === el) {
        hadNativeLightboxFsRef.current = true;
        return;
      }
      if (hadNativeLightboxFsRef.current && lightboxOpenRef.current && !fs) {
        hadNativeLightboxFsRef.current = false;
        setLightboxOpen(false);
        document.body.style.overflow = '';
      }
    };
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs as EventListener);
    onFs();
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs as EventListener);
    };
  }, [lightboxOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        void closeLightbox();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, closeLightbox]);

  lightboxOpenRef.current = lightboxOpen;

  const showNoTokenGate = !loading && !token;
  const showInvalidSessionGate = !loading && token && sessionInvalid;
  const showDeck = pages.length > 0;

  return (
    <div className={`fund-deck-page${showDeck ? ' fund-deck-page--viewer' : ''}`}>
      {showDeck ? (
        <>
        <div className="fund-deck-app" onContextMenu={(e) => e.preventDefault()}>
          <header className="fund-deck-bar" onContextMenu={(e) => e.preventDefault()}>
            <div className="fund-deck-bar-title">
              Kyro India Opportunities Fund – I
              <span className="fund-deck-bar-scheme">(scheme of Kyro Alternative Investment Trust – Category-II AIF) · SEBI Reg. No. IN/AIF2/26-27/2191</span>
              <span className="fund-deck-bar-sub">Private · Eligible investors</span>
            </div>
            <div className="fund-deck-bar-actions">
              <button type="button" onClick={openLightbox} aria-label="Open slide in larger view">
                <>
                  <FaExpand style={{ marginRight: 6, verticalAlign: 'middle' }} aria-hidden />
                  Larger view
                </>
              </button>
              <Link to="/asset-management">Close</Link>
            </div>
          </header>
          <div
            className={`fund-deck-load-track${loadBarCompact ? ' fund-deck-load-track--compact' : ''}`}
            role="status"
            aria-live="polite"
            aria-label="Deck loading progress"
            onContextMenu={(e) => e.preventDefault()}
          >
            <div className="fund-deck-load-bar" aria-hidden>
              <div className="fund-deck-load-bar-fill" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="fund-deck-load-meta">
              {loadComplete ? (
                <span>
                  All slides ready · {pages.length} page{pages.length === 1 ? '' : 's'}
                  {pages.length > 1 ? ` · viewing ${currentSlideIndex + 1} of ${pages.length}` : null}
                </span>
              ) : (
                <>
                  <span>
                    Decoding slides {slidesReadyCount} / {pages.length}
                    {knownTotalBytes > 0
                      ? ` · ${formatDeckBytes(knownReadyBytes)} / ${formatDeckBytes(knownTotalBytes)}`
                      : knownTotalBytes === 0 && slideByteSizes
                        ? ' · size estimate unavailable (loading by slide)'
                        : null}
                  </span>
                  <span className="fund-deck-load-pct">{progressPct}%</span>
                </>
              )}
            </div>
          </div>
          <div
            className="fund-deck-carousel"
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            {/* Hidden loaders so decode progress still tracks the full deck; browser cache serves the visible slide. */}
            <div className="fund-deck-prefetch" aria-hidden="true">
              {pages.map((url, i) => {
                if (i === 0) return null;
                if (!bgPrefetchOn) return null;
                return (
                  <img
                    key={`prefetch-${url}`}
                    src={url}
                    alt=""
                    draggable={false}
                    loading="eager"
                    decoding="async"
                    className="fund-deck-prefetch-img"
                    onLoad={() => markSlideDone(i)}
                    onError={() => markSlideDone(i)}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                );
              })}
            </div>

            <div
              className="fund-deck-stage-outer"
              onTouchStart={onSwipeTouchStart}
              onTouchEnd={onSwipeTouchEnd}
              onTouchCancel={() => {
                swipeStartX.current = null;
              }}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div
                className="fund-deck-stage"
                role="group"
                aria-roledescription="carousel"
                aria-busy={!activeFrameReady}
                aria-label={`Fund deck slides, slide ${currentSlideIndex + 1} of ${pages.length}`}
              >
                <div
                  className={`fund-deck-slide-view${activeFrameReady ? ' fund-deck-slide-view--ready' : ' fund-deck-slide-view--pending'}`}
                >
                  {!activeFrameReady && (
                    <>
                      <div className="fund-deck-slide-skeleton" aria-hidden />
                      <div className="fund-deck-slide-loader-wrap">
                        <div className="fund-deck-slide-spinner" aria-hidden />
                        <span className="fund-deck-slide-loader-label">Loading slide…</span>
                      </div>
                    </>
                  )}
                  <img
                    key={`view-${currentSlideIndex}-${pages[currentSlideIndex]}`}
                    src={pages[currentSlideIndex]}
                    alt={`Slide ${currentSlideIndex + 1} of ${pages.length}`}
                    draggable={false}
                    decoding="async"
                    className={
                      activeFrameReady ? 'fund-deck-slide-img fund-deck-slide-img--visible' : 'fund-deck-slide-img'
                    }
                    onLoad={() => {
                      const idx = currentSlideIndex;
                      markSlideDone(idx);
                      setActiveFrameReady(true);
                    }}
                    onError={() => {
                      const idx = currentSlideIndex;
                      markSlideDone(idx);
                      setActiveFrameReady(true);
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                </div>
              </div>
            </div>

            {pages.length > 1 && (
              <nav className="fund-deck-carousel-nav" aria-label="Slide navigation">
                <button
                  type="button"
                  className="fund-deck-carousel-btn"
                  onClick={goPrevSlide}
                  disabled={currentSlideIndex <= 0}
                  aria-label="Previous slide"
                >
                  <FaChevronLeft aria-hidden />
                  <span>Previous</span>
                </button>
                <span className="fund-deck-carousel-counter" aria-live="polite">
                  {currentSlideIndex + 1} <span className="fund-deck-carousel-counter-sep">/</span> {pages.length}
                </span>
                <button
                  type="button"
                  className="fund-deck-carousel-btn"
                  onClick={goNextSlide}
                  disabled={currentSlideIndex >= pages.length - 1}
                  aria-label="Next slide"
                >
                  <span>Next</span>
                  <FaChevronRight aria-hidden />
                </button>
              </nav>
            )}
          </div>
        </div>
        {lightboxOpen &&
          createPortal(
            <div
              ref={lightboxRef}
              className="fund-deck-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label="Expanded slide"
              onContextMenu={(e) => e.preventDefault()}
            >
              <div className="fund-deck-lightbox-chrome">
                <span className="fund-deck-lightbox-hint">Expanded slide · Esc to close</span>
                <button
                  type="button"
                  className="fund-deck-lightbox-close"
                  onClick={() => void closeLightbox()}
                  aria-label="Close expanded slide"
                >
                  <FaTimes style={{ marginRight: 6, verticalAlign: 'middle' }} aria-hidden />
                  Close
                </button>
              </div>
              <div
                className="fund-deck-lightbox-stage"
                onTouchStart={onSwipeTouchStart}
                onTouchEnd={onSwipeTouchEnd}
                onTouchCancel={() => {
                  swipeStartX.current = null;
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img
                  key={`lb-${currentSlideIndex}-${pages[currentSlideIndex]}`}
                  src={pages[currentSlideIndex]}
                  alt={`Slide ${currentSlideIndex + 1} of ${pages.length}`}
                  className="fund-deck-lightbox-img"
                  draggable={false}
                  decoding="async"
                  onLoad={() => markSlideDone(currentSlideIndex)}
                  onError={() => markSlideDone(currentSlideIndex)}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
              </div>
              {pages.length > 1 && (
                <nav className="fund-deck-lightbox-nav" aria-label="Expanded slide navigation">
                  <button
                    type="button"
                    className="fund-deck-lightbox-nav-btn"
                    onClick={goPrevSlide}
                    disabled={currentSlideIndex <= 0}
                    aria-label="Previous slide"
                  >
                    <FaChevronLeft aria-hidden />
                    <span>Previous</span>
                  </button>
                  <span className="fund-deck-lightbox-counter" aria-live="polite">
                    {currentSlideIndex + 1} / {pages.length}
                  </span>
                  <button
                    type="button"
                    className="fund-deck-lightbox-nav-btn"
                    onClick={goNextSlide}
                    disabled={currentSlideIndex >= pages.length - 1}
                    aria-label="Next slide"
                  >
                    <span>Next</span>
                    <FaChevronRight aria-hidden />
                  </button>
                </nav>
              )}
            </div>,
            document.body
          )}
        </>
      ) : (
        <>
          {loading && <div className="fund-deck-state">Loading…</div>}

          {error && <div className="fund-deck-state fund-deck-state--error">{error}</div>}

          {processing && !loading && (
            <div className="fund-deck-state fund-deck-state--processing">
              <p>Your access is confirmed.</p>
              <p>Loading slide images… This can take a few seconds while the deck is prepared.</p>
            </div>
          )}

          {showNoTokenGate && (
            <div className="fund-deck-gate-wrap">
              <div className="fund-deck-gate">
                <h1>Access required</h1>
                <p>Submit your details once to open the fund deck in this browser.</p>
                <button type="button" className="fund-deck-btn-primary" onClick={() => openDeckModal()}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {showInvalidSessionGate && (
            <div className="fund-deck-gate-wrap">
              <div className="fund-deck-gate">
                <h1>Session ended</h1>
                <p>Submit your details again to continue.</p>
                <button type="button" className="fund-deck-btn-primary" onClick={() => openDeckModal()}>
                  Continue
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FundDeck;
